import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Truck, Plus, Route, Users, Car, CheckCircle2, Clock, Loader2, MapPin, Calendar, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { STATUS_ROTA, TIPOS_OPERACAO, badge } from "./_lib";
import { DeleteButton } from "../../components/DeleteButton";

export default function RoteirizacaoDashboard() {
  const [kpis, setKpis] = useState(null);
  const [rotas, setRotas] = useState([]);
  const [loading, setLoading] = useState(true);

  const carregar = async () => {
    setLoading(true);
    try {
      const [k, r] = await Promise.all([
        api.get("/roteirizacao/dashboard"),
        api.get("/roteirizacao/rotas", { params: { limit: 20 } }),
      ]);
      setKpis(k.data);
      setRotas(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { carregar(); }, []);

  const tipoLabel = (v) => TIPOS_OPERACAO.find((t) => t.v === v)?.label || v;

  return (
    <div className="space-y-5" data-testid="roteirizacao-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Truck size={28} style={{ color: "#1B3A6B" }} />
          <div>
            <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
              Operações &amp; Roteirização
            </h1>
            <p className="text-sm text-muted-foreground">Organize rotas, motoristas, veículos e operações.</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/app/roteirizacao/motoristas">
            <Button variant="outline" data-testid="btn-motoristas"><Users size={14} className="mr-1" /> Motoristas</Button>
          </Link>
          <Link to="/app/roteirizacao/veiculos">
            <Button variant="outline" data-testid="btn-veiculos"><Car size={14} className="mr-1" /> Veículos</Button>
          </Link>
          <Link to="/app/roteirizacao/utilizacao">
            <Button variant="outline" data-testid="btn-utilizacao"><BarChart3 size={14} className="mr-1" /> Utilização</Button>
          </Link>
          <Link to="/app/roteirizacao/rotas/nova">
            <Button className="text-white" style={{ background: "#F97316" }} data-testid="btn-nova-rota">
              <Plus size={14} className="mr-1" /> Nova Rota
            </Button>
          </Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 border-l-4 border-l-blue-500" data-testid="kpi-rotas-hoje">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={12} /> Rotas hoje</div>
          <div className="text-2xl font-bold">{kpis?.rotas_hoje ?? "—"}</div>
        </Card>
        <Card className="p-3 border-l-4 border-l-amber-500" data-testid="kpi-em-andamento">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> Em andamento</div>
          <div className="text-2xl font-bold text-amber-700">{kpis?.em_andamento ?? "—"}</div>
        </Card>
        <Card className="p-3 border-l-4 border-l-emerald-500" data-testid="kpi-concluidas">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 size={12} /> Concluídas</div>
          <div className="text-2xl font-bold text-emerald-700">{kpis?.concluidas ?? "—"}</div>
        </Card>
        <Card className="p-3 border-l-4 border-l-violet-500" data-testid="kpi-clientes-prog">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={12} /> Clientes programados</div>
          <div className="text-2xl font-bold text-violet-700">{kpis?.clientes_programados ?? "—"}</div>
        </Card>
        <Card className="p-3 border-l-4 border-l-zinc-400" data-testid="kpi-motoristas">
          <div className="text-xs text-muted-foreground">Motoristas ativos</div>
          <div className="text-2xl font-bold">{kpis?.motoristas_ativos ?? "—"}</div>
        </Card>
        <Card className="p-3 border-l-4 border-l-zinc-400" data-testid="kpi-veiculos">
          <div className="text-xs text-muted-foreground">Veículos ativos</div>
          <div className="text-2xl font-bold">{kpis?.veiculos_ativos ?? "—"}</div>
        </Card>
        <Card className="p-3 border-l-4 border-l-blue-300" data-testid="kpi-planejadas">
          <div className="text-xs text-muted-foreground">Planejadas</div>
          <div className="text-2xl font-bold">{kpis?.planejadas ?? "—"}</div>
        </Card>
      </div>

      {/* Lista de rotas */}
      <Card>
        <div className="p-3 border-b">
          <h2 className="font-bold flex items-center gap-2" style={{ color: "#1B3A6B" }}>
            <Route size={18} /> Rotas recentes
          </h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Número</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Motorista</TableHead>
              <TableHead>Veículo</TableHead>
              <TableHead className="text-center">Paradas</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={8} className="text-center py-8">
                <Loader2 className="animate-spin inline" /> Carregando...
              </TableCell></TableRow>
            )}
            {!loading && rotas.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                Nenhuma rota cadastrada. Clique em <strong>Nova Rota</strong> para começar.
              </TableCell></TableRow>
            )}
            {!loading && rotas.map((r) => {
              const b = badge(r.status, STATUS_ROTA);
              return (
                <TableRow key={r.id} data-testid={`rota-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">{r.numero}</TableCell>
                  <TableCell>{r.data}</TableCell>
                  <TableCell>{tipoLabel(r.tipo_operacao)}</TableCell>
                  <TableCell className="text-xs">{r.motorista_nome || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{r.veiculo_placa || "—"}</TableCell>
                  <TableCell className="text-center">{r.paradas?.length || 0}</TableCell>
                  <TableCell><Badge className={b.cls + " border"}>{b.label}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Link to={`/app/roteirizacao/rotas/${r.id}`}>
                        <Button size="sm" variant="outline" data-testid={`abrir-rota-${r.id}`}>Abrir</Button>
                      </Link>
                      <DeleteButton
                        url={`/roteirizacao/rotas/${r.id}`}
                        label={`Rota ${r.numero || r.id.slice(0, 8)}`}
                        onDeleted={carregar}
                        testId={`del-rota-${r.id}`}
                        iconOnly
                      />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
